import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

// Founder blueprint reading — brief by default, expands to full on click
function FounderFullReading() {
  const italicSerif = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontStyle: 'italic', margin: '0 0 14px', lineHeight: 1.7, color: 'rgba(250,248,245,0.88)' };
  const em = { color: 'var(--cream)', fontStyle: 'normal', fontWeight: 500 };

  return (
    <div>
      <p style={{ ...italicSerif, fontSize: 19, color: 'rgba(250,248,245,0.92)', marginBottom: 14 }}>
        A <strong style={em}>Life Path 33/6</strong> walks the path of the <strong style={em}>master teacher and nurturer</strong> — here to raise the emotional literacy of everyone they touch.
      </p>
      <p style={{ ...italicSerif, fontSize: 18, margin: '0 0 14px' }}>
        With a <strong style={em}>Soul Urge 44/8</strong>, I crave the quiet work of <strong style={em}>building something that lasts and heals</strong>. My <strong style={em}>Expression 5</strong> gives me the freedom and adaptability to reinvent, while my <strong style={em}>Personality 6</strong> makes people feel safe the moment they sit down.
      </p>
      <p style={{ ...italicSerif, fontSize: 18, margin: '0 0 4px' }}>
        A <strong style={em}>Birthday 7</strong> brings depth and intuition; <strong style={em}>Maturity 11/2</strong> is the spiritual teacher crystallising — the voice I was always meant to find. <strong style={em}>Scorpio depth</strong> is the fire underneath it all.
      </p>
    </div>
  );
}

// Founder story with Read-more expand. First sentence renders title-size.
function FounderStory() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="founder-body" data-testid="founder-story">
      <p className="founder-lead">I've spent my whole life trying to understand why we are all so different.</p>

      <p className="founder-para">
        From the moment I could read, I was reading people. Their patterns. Their rhythms.
        The way certain souls light up in specific circumstances and completely shut down in others.
      </p>

      <p className="founder-para">
        I became a hairdresser at fifteen because my parents saw something in me.
        They always encouraged me to explore the things I showed a natural talent for.
        And hairdressing turned out to be the best thing that ever happened to me.
      </p>

      {expanded && (
        <>
          <p className="founder-para">
            Because it brought together everything I love.
            People. Creativity. Transformation. Connection.
            And a chair where people felt safe enough to tell me the truth.
          </p>

          <p className="founder-para">
            For over twenty years I've listened to what people don't say out loud.
            The quiet confusion of a parent who loves their child completely
            but can't quite reach them.
          </p>

          <p className="founder-para founder-highlight">
            I've always believed that who we are isn't a mystery. It's a blueprint.
            It was there the moment we arrived.
            In our name. In our date of birth.
            In the numbers that have followed us quietly our whole lives.
          </p>

          <p className="founder-para">
            I'm a mother of three. Each of them completely different.
            Each of them teaching me something the others couldn't.
          </p>

          <p className="founder-para">
            So I built Little Blue Numbers.
            Because every parent deserves to feel that moment
            when the numbers describe your child so precisely
            that something in your chest releases.
          </p>

          <p className="founder-para">
            I'm not a perfect parent. I'm a curious one.
            And curiosity, in my experience,
            is the most powerful thing you can bring to the people you love.
          </p>
        </>
      )}

      <div style={{ marginTop: 20 }}>
        <button
          className="founder-readmore"
          onClick={() => setExpanded(v => !v)}
          data-testid="founder-readmore"
        >
          {expanded ? 'Read less ↑' : 'Read more ↓'}
        </button>
      </div>

      <span className="founder-sig">Jemma Di Lorito — Founder, Little Blue Numbers</span>
    </div>
  );
}

const NUMBER_INFO = {
  LP: { label: 'Life Path', code: 'LP', color: 'blue', title: "The Soul's Blueprint", keywords: ['Purpose', 'Direction', 'Core Nature'], desc: "The Life Path is the most significant number in your blueprint. It reveals the core of who you are — your soul's main lesson, the life you came here to live, and the qualities you're here to develop. It's not about what you do, but who you are at your deepest." },
  EX: { label: 'Expression', code: 'EX', color: 'mauve', title: 'How You Show Up', keywords: ['Natural Gifts', 'Approach', 'Talents'], desc: "Your Expression number reveals your natural talents, your instinctive ways of engaging with the world, and the gifts you were born with. Where Life Path is who you are, Expression is how you operate — your way of working, creating, and moving through relationships." },
  SU: { label: 'Soul Urge', code: 'SU', color: 'blue', title: "The Heart's Desire", keywords: ['Deep Needs', 'Inner Longing', 'True Motivation'], desc: "Hidden beneath the surface, the Soul Urge is what the heart truly craves — often misunderstood, rarely spoken aloud. For children especially, understanding this number is one of the most compassionate gifts a parent can have." },
  PE: { label: 'Personality', code: 'PE', color: 'mauve', title: 'The Face You Show', keywords: ['First Impressions', 'Outer Self', 'How Others See You'], desc: "Your Personality number describes the version of you that the world first meets — the face shown before trust is built. Often quite different from your inner self, this number explains why others sometimes misread you." },
  BD: { label: 'Birthday', code: 'BD', color: 'blue', title: 'The Hidden Gift', keywords: ['Special Talent', 'Natural Ability', 'Born With'], desc: "Your Birthday number holds a special talent or natural ability you arrived with — something so effortless it may feel ordinary, even though others notice it immediately. It's the quiet gift that often gets overlooked." },
  MA: { label: 'Maturity', code: 'MA', color: 'mauve', title: 'The Unfolding Purpose', keywords: ['Long Game', 'Deeper Calling', 'The Person Becoming'], desc: "The Maturity number reveals the deeper purpose that crystallises over time — the 'why' behind everything that becomes clearer as you grow. In children, it's the person they are becoming. In adults, it's the calling that grows louder." },
  PY: { label: 'Personal Year', code: 'PY', color: 'blue', title: "This Year's Invitation", keywords: ['Current Energy', 'Right Now', 'Timing'], desc: "Your Personal Year number shifts each year and reveals the theme of your current chapter — what this specific year is asking, where energy is flowing, and what to focus on. It moves in a nine-year cycle." },
  STAR: { label: 'Star Sign', code: '☆', color: 'mauve', title: 'The Cosmic Flavour', keywords: ['Element', 'Emotional Style', 'Natural Rhythm'], desc: "While numerology forms the core of your blueprint, your star sign adds texture and flavour — like the key that colours everything else. It describes your instinctive responses and emotional style." },
};

const NUMBERS = [
  { key: 'LP', name: 'Life Path', desc: "Soul's main journey and who they are at their core", color: 'blue' },
  { key: 'EX', name: 'Expression', desc: "How they naturally show up in the world day to day", color: 'mauve' },
  { key: 'SU', name: 'Soul Urge', desc: "What their heart truly needs at their core", color: 'blue' },
  { key: 'PE', name: 'Personality', desc: "The face they show the world — often different to home", color: 'mauve' },
  { key: 'BD', name: 'Birthday', desc: "A secondary gift or hidden talent from their birth day", color: 'blue' },
  { key: 'MA', name: 'Maturity', desc: "The deeper purpose that crystallises as they grow", color: 'mauve' },
  { key: 'PY', name: 'Personal Year', desc: "What this specific year is asking of them right now", color: 'blue' },
  { key: 'STAR', name: 'Star Sign', desc: "Woven through the whole reading as flavour and texture", color: 'mauve' },
];

function NumberModal({ info, onClose }) {
  if (!info) return null;
  return (
    <div className="modal-overlay" onClick={onClose} data-testid="number-modal">
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        <div className="modal-icon">{info.code}</div>
        <div className="modal-label">{info.label}</div>
        <div className="modal-title">{info.title}</div>
        <div className="modal-keywords">{info.keywords.map(k => <span key={k} className="modal-kw">{k}</span>)}</div>
        <div className="modal-desc">{info.desc}</div>
        <button className="modal-close" onClick={onClose} data-testid="modal-close-btn">Got it</button>
      </div>
    </div>
  );
}

function FeedbackModal({ onClose }) {
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/feedback`, { message, name, email });
      setDone(true);
    } catch { setDone(true); }
    finally { setLoading(false); }
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose} data-testid="feedback-modal-overlay">
      <div className="auth-modal-box" onClick={e => e.stopPropagation()} data-testid="feedback-modal">
        <button className="auth-close" onClick={onClose}>×</button>
        {!done ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 300, color: 'var(--cream)', marginBottom: 4 }}>Share your thoughts</div>
              <div style={{ fontSize: 13, color: 'rgba(250,248,245,0.6)' }}>We read every message. Thank you for taking the time.</div>
            </div>
            <form onSubmit={handleSubmit}>
              <label className="field-label" style={{ color: 'rgba(250,248,245,0.5)' }}>Your message</label>
              <textarea
                style={{ width: '100%', border: '1.5px solid rgba(250,248,245,0.2)', borderRadius: 13, padding: '13px 16px', fontFamily: "'Figtree', sans-serif", fontSize: 15, color: 'var(--cream)', background: 'rgba(255,255,255,0.08)', outline: 'none', resize: 'vertical', minHeight: 100, lineHeight: 1.6 }}
                value={message} onChange={e => setMessage(e.target.value)}
                placeholder="What did you think? What would you love to see?"
                required data-testid="feedback-message"
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div>
                  <label className="field-label" style={{ color: 'rgba(250,248,245,0.5)' }}>Name (optional)</label>
                  <input className="field-input" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(250,248,245,0.2)', color: 'var(--cream)' }} value={name} onChange={e => setName(e.target.value)} placeholder="First name" data-testid="feedback-name" />
                </div>
                <div>
                  <label className="field-label" style={{ color: 'rgba(250,248,245,0.5)' }}>Email (optional)</label>
                  <input className="field-input" type="email" style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(250,248,245,0.2)', color: 'var(--cream)' }} value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" data-testid="feedback-email" />
                </div>
              </div>
              <button type="submit" disabled={loading || !message.trim()} style={{ width: '100%', background: 'rgba(250,248,245,0.12)', border: '1.5px solid rgba(250,248,245,0.25)', borderRadius: 100, padding: 14, fontFamily: "'Figtree', sans-serif", fontSize: 14, color: 'var(--cream)', cursor: 'pointer', marginTop: 16, opacity: loading ? 0.5 : 1 }} data-testid="feedback-submit">
                {loading ? 'Sending...' : 'Send feedback'}
              </button>
            </form>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, color: 'var(--cream)', marginBottom: 10 }}>Thank you</div>
            <p style={{ fontSize: 15, color: 'rgba(250,248,245,0.6)', lineHeight: 1.7 }}>Your feedback means everything to us. We read every single message.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [modalInfo, setModalInfo] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  return (
    <>
      {/* HERO */}
      <div className="hero-wrap">
        <div className="hero">
          <div>
            <div className="hero-tag">Instant · Personalised · Tailored to their stage</div>
            <h1>Everyone<br />is born with<br />a <em>blueprint.</em></h1>
            <p className="hero-sub">
              <strong>Their name and date of birth reveal everything.</strong><br />
              Who they are. How they show up. What they need.<br />
              Written for exactly where they are right now.
            </p>
            <button className="tool-btn" onClick={() => navigate('/tool')} data-testid="hero-cta-btn">Get your reading →</button>
          </div>
          <div>
            <div className="hero-photo-card" data-testid="hero-photo-card">
              <img
                src="https://customer-assets.emergentagent.com/job_life-path-reader-3/artifacts/ykfkx5nt_IMG_8379.jpg"
                alt="A child wrapped in soft cloth, surrounded by glowing numerology numbers"
                loading="eager"
              />
              <figcaption className="hero-photo-card-caption">
                Their name and date of birth tell you exactly who they are.
              </figcaption>
            </div>
          </div>
        </div>
      </div>


      {/* WHY IT WORKS */}
      <section className="section why-it-works">
        <div className="section-label">Why it works</div>
        <h2>You already know there&rsquo;s something<br /><em>special</em> about your child.</h2>
        <p className="section-sub why-sub">
          Why they need so much space. Or so much closeness.<br />
          Why the parenting advice that works for everyone else doesn't quite work for them.<br /><br />
          <strong>Their numbers know why.</strong>
        </p>
        <p className="why-tagline">
          <em>And so do yours. Read for your child first — then read your own.</em>
        </p>
      </section>

      {/* HERO PHOTO — sits after the "why it works" copy so the photo lands as a felt confirmation of the words above */}
      <section className="section full hero-photo-section" data-testid="hero-photo-section">
        <div className="section-inner" style={{ textAlign: 'center', maxWidth: 760 }}>
          <figure className="founding-photo" data-testid="hero-photo">
            <img
              src="https://customer-assets.emergentagent.com/job_life-path-reader-3/artifacts/0yvbxfw9_IMG_8376.PNG"
              alt="A mother and her young son sharing a tender moment on the beach at golden hour"
              loading="lazy"
            />
            <figcaption>They were born wrapped in numbers. We help you read them.</figcaption>
          </figure>
        </div>
      </section>



      {/* HOW IT WORKS */}
      <section className="section" id="how-section">
        <div className="section-label">How it works</div>
        <h2>Three steps.<br /><em>One complete picture.</em></h2>
        <div className="steps">
          <div className="step">
            <div className="step-num">01</div>
            <div className="step-title">Enter name + date of birth</div>
            <p className="step-body">That's all we need. <strong>Their numbers are already there.</strong> We just reveal them.</p>
          </div>
          <div className="step">
            <div className="step-num">02</div>
            <div className="step-title">Select their life stage</div>
            <p className="step-body"><strong>A newborn reading is completely different to a teen reading.</strong> We write for where they are right now.</p>
          </div>
          <div className="step">
            <div className="step-num">03</div>
            <div className="step-title">Receive their blueprint</div>
            <p className="step-body"><strong>Who they are at their core.</strong> How they show up. What their heart needs. How to love them well.</p>
          </div>
        </div>
      </section>



      {/* FOUNDING MEMBERS — value layer + emotional hook + CTA */}
      <section className="section full founding-section" id="founding-section" data-testid="founding-section">
        <div className="founding-inner">
          <div className="founding-copy">
            <div className="founding-badge" data-testid="founding-badge">
              <span aria-hidden="true">✦</span>
              <span>100 founding spots only</span>
            </div>

            <h2 style={{ marginBottom: 14, textAlign: 'center' }}>The first hundred<br />get to <em>shape</em> what we build.</h2>
            <p className="section-sub" style={{ maxWidth: 580, margin: '0 auto 48px', textAlign: 'center' }}>
              Little Blue Numbers is in its quiet beginnings. The full version is coming —
              and the people who stand with us now will be folded into something deeper than
              a typical &ldquo;sign up for updates&rdquo;.
            </p>

            {/* Group 1 — what they get right away */}
            <div className="founding-group-label">What you&rsquo;ll have, right away</div>
            <div className="founding-perks" data-testid="founding-perks">
              <div className="founding-perk">
                <span className="founding-perk-icon" aria-hidden="true">✦</span>
                <div>
                  <strong>A founding-member discount, locked in for life</strong>
                  <span>When subscriptions open, you&rsquo;ll pay an early-believer rate for as long as you stay with us — even as our prices rise.</span>
                </div>
              </div>
              <div className="founding-perk">
                <span className="founding-perk-icon" aria-hidden="true">✦</span>
                <div>
                  <strong>A direct line to me</strong>
                  <span>Founding members shape what we build next. Your questions, your feedback, your real life — all of it makes it in.</span>
                </div>
              </div>
            </div>

            {/* Group 2 — what unlocks as we grow */}
            <div className="founding-group-label">What unlocks as we grow</div>
            <div className="founding-perks" data-testid="founding-perks-2">
              <div className="founding-perk">
                <span className="founding-perk-icon" aria-hidden="true">✦</span>
                <div>
                  <strong>First access to the full Phase 2 reading</strong>
                  <span>Deeper sections, master-number reveals, name-change layers, the chosen-name story — twice the depth, half the abstraction.</span>
                </div>
              </div>
              <div className="founding-perk">
                <span className="founding-perk-icon" aria-hidden="true">✦</span>
                <div>
                  <strong>Full family profiles &amp; saved blueprints</strong>
                  <span>Build a private dashboard for everyone you love. Watch the patterns emerge across generations.</span>
                </div>
              </div>
              <div className="founding-perk">
                <span className="founding-perk-icon" aria-hidden="true">✦</span>
                <div>
                  <strong>Compatibility &amp; relationship readings</strong>
                  <span>See how your blueprint meets your child&rsquo;s — where you click, where you clash, how to soften the edges.</span>
                </div>
              </div>
              <div className="founding-perk">
                <span className="founding-perk-icon" aria-hidden="true">✦</span>
                <div>
                  <strong>An annual Personal Year letter</strong>
                  <span>Each birthday, a warm note arrives in your inbox naming the chapter you&rsquo;re stepping into.</span>
                </div>
              </div>
            </div>

            <div className="founding-hook" data-testid="founding-hook">
              Start with one reading for someone you love. If it sees them — <em>really</em> sees them — stand with us as we build what comes next.
            </div>

            <div className="founding-cta-row">
              <button
                className="waitlist-btn founding-cta-btn"
                onClick={() => navigate('/join')}
                data-testid="founding-cta-btn"
              >
                Join as a founding member
              </button>
              <span className="founding-cta-meta">
                Just your email. No commitment.
              </span>
            </div>
          </div>
        </div>
      </section>



      {/* WHAT WE READ */}
      <section className="section full bg-blue-soft" data-testid="numbers-section">
        <div className="section-inner">
          <div className="section-label">What we read</div>
          <h2>Eight numbers.<br /><em>One complete picture.</em></h2>
          <p className="section-sub">Every number reveals a different layer. <strong>Together they tell the whole story.</strong></p>
          <div className="numbers-grid">
            {NUMBERS.map(n => (
              <div key={n.key} className="number-card" onClick={() => setModalInfo(NUMBER_INFO[n.key])} data-testid={`number-card-${n.key}`}>
                <div className={`num-code ${n.color}`}>{NUMBER_INFO[n.key].code}</div>
                <div className="num-name">{n.name}</div>
                <div className="num-desc">{n.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* QUIET MOMENT — sleeping newborn before testimonials */}
      <section className="section full image-moment" data-testid="image-moment-newborn">
        <div className="section-inner" style={{ maxWidth: 720, textAlign: 'center' }}>
          <figure className="founding-photo">
            <img
              src="https://customer-assets.emergentagent.com/job_life-path-reader-3/artifacts/hlf1caec_B3B71928-B1B2-44B2-868B-A5A80E50BAEA.PNG"
              alt="A sleeping newborn baby surrounded by softly glowing numbers"
              loading="lazy"
            />
            <figcaption>Born already whole. Already named. Already known.</figcaption>
          </figure>
        </div>
      </section>


      {/* TESTIMONIALS — early reader quotes */}
      <section className="section full" style={{ background: 'linear-gradient(180deg, var(--cream) 0%, var(--mauve-pale) 100%)', paddingTop: 80, paddingBottom: 80 }}>
        <div className="section-inner" style={{ textAlign: 'center' }}>
          <div className="section-label" style={{ justifyContent: 'center' }}>From early readers</div>
          <h2 style={{ marginBottom: 14 }}>Words from the<br /><em>first families.</em></h2>
          <p className="section-sub" style={{ maxWidth: 580, margin: '0 auto 56px' }}>
            What parents and adults are saying after their first Little Blue Numbers reading.
          </p>
          <div className="testimonial-grid" data-testid="testimonials-grid">
            {[
              {
                quote: "I cried reading this about my four-year-old. It is the first time I have read something about her that genuinely sounds like her — not a generic personality test. I feel like I understand how to mother her now.",
                name: "Sarah",
                where: "Perth, WA",
                stage: "Mum to a Preschool 7",
              },
              {
                quote: "I have done every personality test going. This was the only one that named the exact thing I have been wrestling with this year. The shadow section made me sit with my coffee and properly think.",
                name: "Lauren",
                where: "Melbourne, VIC",
                stage: "Self reading · Millennial",
              },
              {
                quote: "We did one for our teen and read it together. He actually let me hug him after. The how-to-love-them-well section gave me language I have been searching for for years.",
                name: "Daniel",
                where: "Auckland, NZ",
                stage: "Dad to a Teen 11/2",
              },
            ].map((t, i) => (
              <figure key={i} className="testimonial-card" data-testid={`testimonial-${i}`}>
                <div className="testimonial-mark" aria-hidden="true">&ldquo;</div>
                <blockquote className="testimonial-quote">{t.quote}</blockquote>
                <figcaption className="testimonial-meta">
                  <span className="testimonial-name">{t.name}</span>
                  <span className="testimonial-where">{t.where}</span>
                  <span className="testimonial-stage">{t.stage}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>



      {/* SELF READING */}
      <section className="section full bg-cream2">
        <div className="section-inner">
          <div className="self-card">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 160, height: 160, borderRadius: '50%', background: 'linear-gradient(135deg, var(--blue-pale), var(--mauve-pale))', border: '2px solid var(--mauve-mist)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 42, fontWeight: 300, color: 'var(--mauve-dark)', lineHeight: 1 }}>33/6</div>
                <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--muted)' }}>Life Path</div>
              </div>
            </div>
            <div>
              <div className="section-label" style={{ marginBottom: 16 }}>And when you're ready</div>
              <h2 style={{ fontSize: 'clamp(26px, 3vw, 40px)', marginBottom: 14 }}>Your blueprint<br />is waiting <em>too.</em></h2>
              <p className="section-sub" style={{ marginBottom: 16 }}>Your numbers have always been there.<br /><em>You just haven't read them yet.</em></p>
              <button className="tool-btn" onClick={() => navigate('/tool')} data-testid="self-reading-btn">Read my blueprint →</button>
            </div>
          </div>
        </div>
      </section>



      {/* ABOUT / FOUNDER */}
      <section className="section full bg-deep" id="about-section" style={{ paddingTop: 96, paddingBottom: 96 }}>
        <div className="section-inner" style={{ color: 'var(--cream)' }}>
          <div className="founder-layout">
            <div>
              <div className="founder-name">Jemma Di Lorito</div>
              <div className="founder-role">Founder, Little Blue Numbers</div>
              <div className="founder-nums">
                <div className="founder-num-row"><span className="founder-num-label">Life Path</span><span className="founder-num-val">33/6</span></div>
                <div className="founder-num-row"><span className="founder-num-label">Soul Urge</span><span className="founder-num-val">44/8</span></div>
                <div className="founder-num-row"><span className="founder-num-label">Expression</span><span className="founder-num-val">5</span></div>
                <div className="founder-num-row"><span className="founder-num-label">Personality</span><span className="founder-num-val">6</span></div>
                <div className="founder-num-row"><span className="founder-num-label">Birthday</span><span className="founder-num-val">7</span></div>
                <div className="founder-num-row"><span className="founder-num-label">Maturity</span><span className="founder-num-val">11/2</span></div>
                <div className="founder-num-row"><span className="founder-num-label">Personal Year</span><span className="founder-num-val">1</span></div>
                <div className="founder-num-row" style={{ gridColumn: '1/-1' }}><span className="founder-num-label">Star Sign</span><span className="founder-num-val">♏ Scorpio</span></div>
              </div>
              <div style={{ marginTop: 26, paddingTop: 24, borderTop: '1px solid rgba(250,248,245,0.14)' }} data-testid="founder-brief-reading">
                <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(250,248,245,0.55)', marginBottom: 12 }}>My blueprint, briefly</div>
                <FounderFullReading />
              </div>
            </div>
            <FounderStory />
          </div>
        </div>
      </section>

      {/* WONDER MOMENT — curious child before the closing CTA */}
      <section className="section full image-moment" data-testid="image-moment-wonder">
        <div className="section-inner" style={{ maxWidth: 720, textAlign: 'center' }}>
          <figure className="founding-photo">
            <img
              src="https://customer-assets.emergentagent.com/job_life-path-reader-3/artifacts/bpfg95xw_89F03542-01CC-41A5-B131-4785FE96C929.PNG"
              alt="A young child looking up with bright eyes, surrounded by floating numbers"
              loading="lazy"
            />
            <figcaption>Some children come in already remembering who they are. We help you read them.</figcaption>
          </figure>
        </div>
      </section>

      {/* CTA */}
      <div className="cta-section">
        <div className="section-label" style={{ justifyContent: 'center' }}>No account needed to start</div>
        <h2>Ready to read<br />their <em>blueprint?</em></h2>
        <p style={{ fontSize: 16, color: 'var(--muted)', marginBottom: 8 }}>Enter a name and date of birth. That's all it takes.</p>
        <button className="tool-btn" onClick={() => navigate('/tool')} data-testid="cta-start-btn">Start my reading →</button>
      </div>

      {/* FOOTER */}
      <footer>
        <div>© 2026 Little Blue Numbers · Burns Beach, Western Australia</div>
        <div style={{ marginTop: 6, opacity: 0.6 }}>Built with love, for families.</div>
        <div style={{ marginTop: 14, display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', fontSize: 12 }}>
          <a href="/privacy" style={{ color: 'var(--muted)', textDecoration: 'underline' }} data-testid="footer-privacy">Privacy Policy</a>
          <a href="/terms" style={{ color: 'var(--muted)', textDecoration: 'underline' }} data-testid="footer-terms">Terms of Service</a>
          <a href="/faq" style={{ color: 'var(--muted)', textDecoration: 'underline' }} data-testid="footer-faq">FAQ</a>
          <a href="mailto:hello@littlebluenumbers.com" style={{ color: 'var(--muted)', textDecoration: 'underline' }} data-testid="footer-contact">Contact</a>
        </div>
        <div className="footer-trust" data-testid="footer-trust">
          Your details stay private. We never sell, share, or track. Encrypted &amp; safe.
        </div>
      </footer>

      {/* MODALS */}
      {modalInfo && <NumberModal info={modalInfo} onClose={() => setModalInfo(null)} />}
      {showFeedback && <FeedbackModal onClose={() => setShowFeedback(false)} />}
    </>
  );
}
